// experiments.js
// A/B variant ka SINGLE source of truth (client side).
//
// Rule #1: is file me kabhi bhi Math.random() nahi aayega.
// Variant BACKEND assign karta hai — LOGIN ke waqt, EndUsers.experimentVariant
// column me, ek baar. Uske baad wo column kabhi overwrite nahi hota, isliye user
// ko har device, har browser aur har reload pe wahi flow milta hai.
// Agar frontend khud random karta, to har refresh / incognito / doosre device pe
// variant flip ho jaata aur GA4 ka poora comparison bekaar ho jaata.
//
// Value do raston se aati hai:
//   1. JWT — login pe backend token me experimentVariant bhejta hai. Sabse
//      tezz: koi API call nahi, page load pe hi available.
//   2. getUserDetails — un users ke liye fallback jinka 30-din wala token is
//      deploy se PEHLE bana tha (us token me field hai hi nahi).
// Dono na milein to "A" — yaani purana, tested flow. Kabhi random nahi.
//
// Python backend uses Variant A for the Go Live flow, matching the current
// Retell rollout. The QA URL override (?ab=A/B) can still override it.
//
// Backend: src/utils/experiments.js + scripts/add-experiment-variant.js

import decodeToken from "./decodeToken";
import { isPythonBackend } from "../utils/apiBackend";

// Har experiment ka naam yahin rakho taaki typo se do alag keys na ban jaayein.
export const EXPERIMENTS = {
    GO_LIVE_FLOW: "goLiveFlow",
};

// A = purana flow (control)  → /recommended-plan
// B = naya flow              → /receptionist-number → /agent-live
export const VARIANT_A = "A";
export const VARIANT_B = "B";

const STORAGE_PREFIX = "ab_";
// Testing override — URL me ?ab=B lagao to ye variant chipak jaata hai.
const OVERRIDE_KEY = "ab_override";
// Backend ne EXPERIMENT_GO_LIVE_FLOW env se experiment pin kar diya hai.
// Ye key JWT se BHI upar hai — kyunki token 30 din chalta hai aur usme purana
// variant baked hai, to sirf backend badalne se logged-in users pe koi asar
// nahi hota. Server wapas auto pe aaye to key hata di jaati hai.
const FORCED_KEY = "ab_forced";

const storageKey = (name) => `${STORAGE_PREFIX}${name}`;

const readStorage = (key) => {
    try {
        return typeof localStorage === "undefined" ? null : localStorage.getItem(key);
    } catch {
        // private mode / restricted WebView — variant bas default pe chala jayega.
        return null;
    }
};

const removeStorage = (key) => {
    try {
        if (typeof localStorage !== "undefined") localStorage.removeItem(key);
    } catch {
        /* storage blocked — ignore */
    }
};

const writeStorage = (key, value) => {
    try {
        if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
    } catch {
        /* storage blocked — ignore */
    }
};

const normalize = (value) => {
    const v = String(value || "").trim().toUpperCase();
    return v === VARIANT_B ? VARIANT_B : v === VARIANT_A ? VARIANT_A : null;
};

/**
 * Backend se aayi value ko cache kar do.
 *
 * Ise wahan se call karo jahan user details load hoti hain (getUserDetails), taaki
 * variant page load pe turant available ho — navigate karte waqt async wait na
 * karna pade. Backend ke bina value null hoti hai, to hum kuch likhte hi nahi
 * (purani cached value safe rehti hai).
 */
export const setVariantFromServer = (name, value, forced = false) => {
    const v = normalize(value);
    if (!name || !v) return;
    // forced=true  → server ne env se pin kiya hai, JWT ko bhi override karo.
    // forced=false → server wapas normal split pe hai, pinned value hata do.
    if (forced) writeStorage(FORCED_KEY + "_" + name, v);
    else removeStorage(FORCED_KEY + "_" + name);
    writeStorage(storageKey(name), v);
};

/**
 * ?ab=B (ya ?ab=A) URL me ho to use pakad ke chipka do, aur TRUE lauta do.
 *
 * Sirf manual testing ke liye. App start pe ek baar call karo. Ye backend wali
 * value ko override karta hai, isliye production users ko kabhi nahi lagega —
 * unke URL me ye param hota hi nahi.
 */
export const captureVariantOverride = (search) => {
    try {
        const raw = new URLSearchParams(search || "").get("ab");
        const v = normalize(raw);
        if (!v) return false;
        writeStorage(OVERRIDE_KEY, v);
        return true;
    } catch {
        return false;
    }
};

// JWT me backend ne login pe jo variant bheja tha.
const variantFromToken = () => {
    try {
        const token =
            typeof localStorage === "undefined" ? null : localStorage.getItem("token");
        if (!token) return null;
        return normalize(decodeToken(token)?.experimentVariant);
    } catch {
        return null;
    }
};

/**
 * Is user ka variant. Priority:
 *   1. ?ab= override (sirf QA)
 *   2. Python backend (rexpt.in) → "A", matching the Retell rollout
 *   3. server-forced value — backend ka EXPERIMENT_GO_LIVE_FLOW env
 *   4. JWT — login pe backend ne jo assign kiya
 *   5. getUserDetails se cached value (purane tokens ka fallback)
 *   6. "A" — control, yaani jo flow aaj chal raha hai
 *
 * #2 keeps Python independent of stale JWTs and cached B assignments. Node
 * (rexpt.ai) continues to use its server-forced, JWT, and cached assignment.
 *
 * #2 is above #3 so Python always follows Variant A even if its backend
 * returns a forced experiment value.
 *
 * QA override upar isliye rakha hai taaki rexpt.in par bhi ?ab=A se purana flow
 * dekha ja sake.
 *
 * NOTE (GA4): Python reports Variant A by product choice; analyze its funnel
 * separately from the Retell A/B experiment.
 *
 * #3 ko #4 se UPAR rakhna zaroori hai: token 30 din chalta hai aur usme variant
 * baked hota hai, isliye env flip karne pe bhi logged-in user purane flow pe
 * atka reh jaata. Forced value getUserDetails pe refresh hoti hai, to app-load
 * ke turant baad sahi flow lag jaata hai.
 *
 * Value na milne par HAMESHA "A" — kabhi random nahi. Backend down ho ya token
 * purana ho, to user ko purana (safe, tested) flow hi milega.
 */
export const getVariant = (name = EXPERIMENTS.GO_LIVE_FLOW) => {
    return (
        normalize(readStorage(OVERRIDE_KEY)) ||
        (isPythonBackend() ? VARIANT_A : null) ||
        normalize(readStorage(FORCED_KEY + "_" + name)) ||
        variantFromToken() ||
        normalize(readStorage(storageKey(name))) ||
        VARIANT_A
    );
};

export const isVariantB = (name = EXPERIMENTS.GO_LIVE_FLOW) =>
    getVariant(name) === VARIANT_B;
